import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Layout, PageHero } from "@/components/site/Layout";
import { Seo } from "@/components/site/Seo";
import contactHeroImage from "@/assets/atasehir-skyline.webp";
import { getPostBySlug, getLocalizedSlugsForSlug, BlogPost } from "@/lib/blog";
import { blogPostingJsonLd, faqPageJsonLd } from "@/lib/seoSchemas";
import { useLp, useUrlLocale } from "@/lib/localePath";
import { useI18n, pick, type Locale } from "@/lib/i18n";
import { useSetPageLocales } from "@/lib/pageLocales";
import { Calendar, User, ArrowLeft } from "lucide-react";

// Hem TR ("Sık Sorulan Sorular") hem EN ("Frequently Asked Questions") başlığını tanır.
function parseFaq(content: string): { question: string; answer: string }[] {
  const match = content.match(/## (?:Sık Sorulan Sorular|Sıkça Sorulan Sorular|Frequently Asked Questions)\n([\s\S]+?)(?=\n## |$)/);
  if (!match) return [];
  const block = match[1];
  const pairs: { question: string; answer: string }[] = [];
  const re = /### (.+?)\n\n([\s\S]+?)(?=\n### |\n## |$)/g;
  let m;
  while ((m = re.exec(block)) !== null) {
    pairs.push({
      question: m[1].trim(),
      answer: m[2].trim().replace(/\*\*/g, "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1"),
    });
  }
  return pairs;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [postLocales, setPostLocales] = useState<Locale[] | null>(null);
  const [localizedSlugs, setLocalizedSlugs] = useState<Partial<Record<Locale, string>> | null>(null);
  const lp = useLp();
  const { t, locale } = useI18n();
  // URL'den SENKRON okunur (useParams) — useI18n().locale ise LocaleGate'in
  // useEffect'i URL'den güncelleyene kadar bir an için ESKİ değeri taşıyabilir
  // (React child-effect'leri parent-effect'lerden ÖNCE çalışır). Blog içeriği
  // hangi dilde yükleneceğine bu yüzden context'e değil doğrudan URL'e göre
  // karar vermeli — aksi halde ilk render'da yanlış dilde/hiç içerik gelebilir
  // (16 Eylül 2026, çok-dilli blog eklenirken bulundu).
  const urlLocale = useUrlLocale() ?? "tr";
  const setPageLocales = useSetPageLocales();

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getPostBySlug(slug, urlLocale)
      .then(async (p) => {
        if (cancelled) return;
        if (!p) {
          setNotFound(true);
          setPageLocales(null);
          setPostLocales(null);
          setLocalizedSlugs(null);
          return;
        }
        setPost(p);
        // Header'daki dil değiştirici ve hreflang alternate'leri sadece bu
        // yazının gerçekten çevrildiği dilleri + o dildeki KENDİ slug'ını
        // göstersin — aksi halde çevirisi olmayan bir dile geçmek "yazı
        // bulunamadı" ekranına kırık yönlendirme yapar, ya da hreflang
        // paylaşılan TR slug'ını yanlışlıkla "gerçek sürüm" diye işaretler.
        const slugs = await getLocalizedSlugsForSlug(p.canonicalSlug);
        if (cancelled) return;
        const locales = Object.keys(slugs) as Locale[];
        setLocalizedSlugs(slugs);
        setPageLocales(locales);
        setPostLocales(locales);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      setPageLocales(null);
      setPostLocales(null);
    };
  }, [slug, urlLocale, setPageLocales]);

  if (loading) {
    return (
      <Layout>
        <div className="container-page py-20 animate-pulse">
          <div className="h-8 bg-muted rounded w-1/3 mb-6" />
          <div className="h-6 bg-muted rounded w-3/4 mb-12" />
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-4 bg-muted rounded w-full" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (notFound || !post) {
    return (
      <Layout>
        {/* prerender.mjs canonical link bekliyor — Seo çağrısı olmadan build timeout'a düşer. */}
        <Seo title={t("404.title")} path="/blog" />
        <div className="container-page py-20 text-center">
          <h1 className="text-3xl font-bold text-foreground/60">
            {t("404.title")}
          </h1>
          <p className="mt-2 text-foreground/40">
            {pick(urlLocale, {
              tr: "Bu blog yazısı bulunamadı.",
              en: "This blog post could not be found.",
            })}
          </p>
          <Link
            to={lp("/blog")}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-accent hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            {pick(urlLocale, { tr: "Blog'a dön", en: "Back to blog" })}
          </Link>
        </div>
      </Layout>
    );
  }

  const alternatePaths = localizedSlugs
    ? (Object.fromEntries(
        Object.entries(localizedSlugs).map(([l, s]) => [l, `/blog/${s}`]),
      ) as Partial<Record<Locale, string>>)
    : undefined;

  const faqItems = parseFaq(post.content);
  const jsonLd = [
    blogPostingJsonLd({
      slug: slug!,
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      image: post.image,
      author: post.author,
    }),
    ...(faqItems.length > 0 ? [faqPageJsonLd(faqItems)] : []),
  ];

  return (
    <Layout>
      <Seo
        title={`${post.title} — Anadolu Terra Blog`}
        description={post.excerpt}
        path={`/blog/${slug}`}
        image={post.image}
        type="article"
        jsonLd={jsonLd}
        availableLocales={postLocales ?? undefined}
        alternatePaths={alternatePaths}
        datePublished={post.date}
        dateModified={post.date}
      />

      <article>
        {/* Header */}
        <section className="relative overflow-hidden text-primary-foreground">
          <div className="absolute inset-0">
            <img src={contactHeroImage} alt="" className="h-full w-full object-cover opacity-100" />
            <div className="absolute inset-0 bg-primary/35 mix-blend-multiply" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-ink/15 to-transparent" />
          </div>
          <div className="container-page relative py-16 md:py-24">
            <Link
              to={lp("/blog")}
              className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft className="h-4 w-4" />
              {pick(urlLocale, { tr: "Blog'a dön", en: "Back to blog" })}
            </Link>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading tracking-tight leading-tight drop-shadow-lg">
              {post.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-white/60">
              <span className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-1.5">
                <User className="h-4 w-4" />
                {post.author}
              </span>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 md:py-16">
          <div className="container-page max-w-3xl">
            <div className="prose prose-lg dark:prose-invert max-w-none
              prose-headings:font-heading prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground/80 prose-p:leading-relaxed
              prose-strong:text-foreground
              prose-a:text-accent prose-a:no-underline hover:prose-a:underline
              prose-li:text-foreground/80
              prose-code:text-sm prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-ink prose-pre:text-white
              prose-img:rounded-xl
              prose-hr:border-border
            ">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>

            {/* CTA */}
            <div className="mt-16 p-8 rounded-2xl bg-gradient-to-br from-accent/10 to-ink/5 border border-border text-center">
              <h3 className="text-xl font-bold font-heading">
                {pick(urlLocale, {
                  tr: "Güvenlik ihtiyaçlarınız için bizimle iletişime geçin",
                  en: "Get in touch with us for your security needs",
                })}
              </h3>
              <p className="mt-2 text-foreground/70 text-sm">
                {pick(urlLocale, {
                  tr: "Size özel çözümlerimizi keşfetmek için teklif isteyin.",
                  en: "Request a quote to discover our tailored solutions.",
                })}
              </p>
              <Link
                to={lp("/teklif-iste")}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:bg-accent/90 transition-colors"
              >
                {t("nav.quote")} →
              </Link>
            </div>
          </div>
        </section>
      </article>
    </Layout>
  );
}
